// ===========================================
// Student Progress Table Component
// ===========================================
// Table showing detailed student progress data with sorting and pagination

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Pagination,
  TableSortLabel,
} from '@mui/material';

import { StudentProgressRecord } from '../../api/reports.api';
import { formatPercentage } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface StudentProgressTableProps {
  students: StudentProgressRecord[];
  loading?: boolean;
}

type SortField = 'studentName' | 'familyName' | 'lessonsAttended' | 'attendanceRate' | 'progressRate' | 'currentStreak';
type SortOrder = 'asc' | 'desc';

// ===========================================
// COMPONENT
// ===========================================

export default function StudentProgressTable({
  students,
  loading = false,
}: StudentProgressTableProps) {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('studentName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const rowsPerPage = 25;

  // Sort students
  const sortedStudents = [...students].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate
  const paginatedStudents = sortedStudents.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const pageCount = Math.ceil(students.length / rowsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getProgressColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading student progress data...
        </Typography>
      </Box>
    );
  }

  if (students.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No student progress data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'studentName'}
                  direction={sortField === 'studentName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('studentName')}
                >
                  Student
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'familyName'}
                  direction={sortField === 'familyName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('familyName')}
                >
                  Family
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'lessonsAttended'}
                  direction={sortField === 'lessonsAttended' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lessonsAttended')}
                >
                  Lessons
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'attendanceRate'}
                  direction={sortField === 'attendanceRate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('attendanceRate')}
                >
                  Attendance
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Pieces</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'progressRate'}
                  direction={sortField === 'progressRate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('progressRate')}
                >
                  Progress
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'currentStreak'}
                  direction={sortField === 'currentStreak' ? sortOrder : 'asc'}
                  onClick={() => handleSort('currentStreak')}
                >
                  Streak
                </TableSortLabel>
              </TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Instruments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedStudents.map((student) => (
              <TableRow key={student.studentId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {student.studentName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{student.familyName}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {student.lessonsAttended} / {student.lessonsEnrolled}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(student.attendanceRate)}
                    size="small"
                    color={getProgressColor(student.attendanceRate)}
                    sx={{ minWidth: 60 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {student.piecesCompleted} / {student.piecesAssigned}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(student.progressRate)}
                    size="small"
                    color={getProgressColor(student.progressRate)}
                    sx={{ minWidth: 60 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {student.currentStreak} days
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      best: {student.longestStreak}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{student.currentLevel}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {student.instruments.map((instrument, index) => (
                      <Chip
                        key={index}
                        label={instrument}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Summary Footer */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedStudents.length} of {students.length} students
        </Typography>
      </Box>
    </Box>
  );
}
